import React, { lazy, Suspense } from "react";
import { Routes, Route, Navigate, BrowserRouter } from "react-router-dom";
import { NavBar } from "../components/NavBar";
import { SideBar } from "../components/SideBar";
import { useAuth } from "../hooks/auth_context";
import ProtectedRoute from "../ProtectedRoute";
import Footer from "../components/Footer";
import { RouteErrorBoundary } from "../components/ErrorBoundary";

import { Login } from "./Login";
import { Unauthorized } from "./Unauthorized";

// ── Route-level code splitting ──────────────────────────────────────────
// Each admin page is its own chunk. Login and Unauthorized stay eager —
// they are what an unauthenticated visitor renders on first paint.
const Dashboard = lazy(() =>
  import("./Dashboard").then((m) => ({ default: m.Dashboard }))
);
const Customers = lazy(() =>
  import("./Customers").then((m) => ({ default: m.Customers }))
);
const Services = lazy(() =>
  import("./Services").then((m) => ({ default: m.Services }))
);
const Orders = lazy(() =>
  import("./Orders").then((m) => ({ default: m.Orders }))
);
const Gigs = lazy(() =>
  import("./Gigs").then((m) => ({ default: m.Gigs }))
);
const Offers = lazy(() =>
  import("./Offers").then((m) => ({ default: m.Offers }))
);
const Proposals = lazy(() =>
  import("./Proposals").then((m) => ({ default: m.Proposals }))
);
const Withdraw = lazy(() =>
  import("./Withdraw").then((m) => ({ default: m.Withdraw }))
);
const Profile = lazy(() =>
  import("./Profile").then((m) => ({ default: m.Profile }))
);
const Chat = lazy(() =>
  import("./Chat").then((m) => ({ default: m.Chat }))
);


export const Layout = () => {
  const { isLoggedIn } = useAuth();

  return (
    <BrowserRouter>
      <div className="flex min-h-screen bg-[#F5F5F5] font-sans text-[#1A1A1A]">
        {isLoggedIn && <SideBar />}
        <div className={`flex-1 flex flex-col ${isLoggedIn ? "" : "w-full"}`}>
          {isLoggedIn && <NavBar />}

          <main className={isLoggedIn ? "flex-1 px-6 lg:px-8 pb-10" : "flex-1"}>
            {/*
              Boundary sits INSIDE <main>, below SideBar and NavBar. A page
              that throws is contained to this panel — the sidebar and top nav
              keep rendering, so the admin can click straight to another page
              instead of staring at a blank tab.
            */}
            <RouteErrorBoundary>
            <Suspense fallback={<div className="p-8 text-sm text-[#707070]">Loading…</div>}>
            <Routes>
              <Route
                path="/"
                element={<ProtectedRoute Element={<Dashboard />} />}
              />
              <Route
                path="/customers"
                element={<ProtectedRoute Element={<Customers />} />}
              />
              <Route
                path="/services"
                element={<ProtectedRoute Element={<Services />} />}
              />
              <Route
                path="/orders"
                element={<ProtectedRoute Element={<Orders />} />}
              />
              <Route
                path="/gigs"
                element={<ProtectedRoute Element={<Gigs />} />}
              />
              <Route
                path="/offers"
                element={<ProtectedRoute Element={<Offers />} />}
              />
              <Route
                path="/proposals"
                element={<ProtectedRoute Element={<Proposals />} />}
              />
              <Route
                path="/withdraw"
                element={<ProtectedRoute Element={<Withdraw />} />}
              />
              <Route
                path="/profile"
                element={<ProtectedRoute Element={<Profile />} />}
              />
              <Route
                path="/chat"
                element={<ProtectedRoute Element={<Chat />} />}
              />

              <Route path="/login" element={<Login />} />
              <Route path="/unauthorized" element={<Unauthorized />} />

              <Route path="*" element={<Navigate to="/login" replace />} />
            </Routes>
            </Suspense>
            </RouteErrorBoundary>
          </main>
          {isLoggedIn && <Footer />}
        </div>
      </div>
    </BrowserRouter>
  );
};
